import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchTickers, fetchCandles, fetchExchanges } from '../api/client';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import LoginModal from '../components/LoginModal';
import './Explore.css';

import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Filler
);

function Explore() {
  const { isGuest } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [selectedExchange, setSelectedExchange] = useState('all');
  const [timeRange, setTimeRange] = useState('1Y');
  const [volumeFilter, setVolumeFilter] = useState('all');
  const [marketCapFilter, setMarketCapFilter] = useState('all');
  const [selectedCryptos, setSelectedCryptos] = useState([]);
  const [compareMode, setCompareMode] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [showSymbolInput, setShowSymbolInput] = useState(false);
  const [symbolInput, setSymbolInput] = useState('');
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisTimeframe, setAnalysisTimeframe] = useState('1d');

  const { data: tickersData, isLoading: tickersLoading } = useQuery({
    queryKey: ['explore-tickers'],
    queryFn: () => fetchTickers({ limit: 5000 })
  });

  const { data: exchangesData } = useQuery({
    queryKey: ['exchanges'],
    queryFn: fetchExchanges
  });

  const processedCryptos = useMemo(() => {
    if (!tickersData?.results) return [];

    const symbolMap = new Map();

    tickersData.results.forEach((ticker) => {
      const [base, quote] = ticker.market_symbol.split('/');
      if (!base || !quote) return;

      const symbol = base.toUpperCase();
      const price = ticker.last || 0;
      const volume24h = ticker.quote_volume_24h || 0;
      const high24h = ticker.high_24h || price;
      const low24h = ticker.low_24h || price;
      const exchange = ticker.exchange?.name || 'Unknown';
      const exchangeId = ticker.exchange?.exchange_id || '';

      if (!symbolMap.has(symbol)) {
        symbolMap.set(symbol, {
          id: symbol.toLowerCase(),
          symbol,
          name: symbol,
          price: 0,
          high24h: 0,
          low24h: 0,
          volume: 0,
          marketCap: 0,
          exchange,
          exchangeId,
          marketSymbol: ticker.market_symbol
        });
      }

      const crypto = symbolMap.get(symbol);
      crypto.volume += volume24h;

      if (price > crypto.price) {
        crypto.price = price;
        crypto.high24h = high24h;
        crypto.low24h = low24h;
      }

      const estimatedMarketCap = price * (volume24h / price) * 100;
      crypto.marketCap = Math.max(crypto.marketCap, estimatedMarketCap);
    });

    return Array.from(symbolMap.values())
      .map((c) => {
        const change24h = c.high24h && c.low24h
          ? ((c.high24h - c.low24h) / c.low24h) * 100
          : 0;
        return { ...c, change24h };
      });
  }, [tickersData]);

  const exchanges = useMemo(() => {
    if (!exchangesData?.results) return [];
    return exchangesData.results.map((e) => e.name);
  }, [exchangesData]);

  const filteredCryptos = useMemo(() => {
    let filtered = processedCryptos;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.name.toLowerCase().includes(query) ||
          c.symbol.toLowerCase().includes(query)
      );
    }

    if (selectedExchange !== 'all') {
      filtered = filtered.filter((c) => c.exchange === selectedExchange);
    }

    if (volumeFilter !== 'all') {
      const threshold = volumeFilter === 'high' ? 100000000 : 10000000;
      filtered = filtered.filter((c) =>
        volumeFilter === 'high' ? c.volume >= threshold : c.volume < threshold
      );
    }

    if (marketCapFilter !== 'all') {
      const threshold = marketCapFilter === 'high' ? 10000000000 : 1000000000;
      filtered = filtered.filter((c) =>
        marketCapFilter === 'high' ? c.marketCap >= threshold : c.marketCap < threshold
      );
    }

    return filtered;
  }, [processedCryptos, searchQuery, selectedExchange, volumeFilter, marketCapFilter]);

  const handleCryptoSelect = (crypto) => {
    if (compareMode) {
      if (selectedCryptos.find((c) => c.id === crypto.id)) {
        setSelectedCryptos(selectedCryptos.filter((c) => c.id !== crypto.id));
      } else if (selectedCryptos.length < 3) {
        setSelectedCryptos([...selectedCryptos, crypto]);
      }
    } else {
      setSelectedCryptos([crypto]);
    }
  };

  const getLimitForTimeRange = (range) => {
    const limits = {
      '1D': 1,
      '7D': 7,
      '1M': 30,
      '3M': 90,
      '1Y': 365,
      '10Y': 1000
    };
    return limits[range] || 365;
  };

  const firstCrypto = selectedCryptos[0];
  const secondCrypto = selectedCryptos[1];
  const thirdCrypto = selectedCryptos[2];

  const { data: firstCandles, isLoading: firstLoading } = useQuery({
    queryKey: ['candles', firstCrypto?.symbol, timeRange],
    queryFn: () => fetchCandles(firstCrypto.symbol, { limit: getLimitForTimeRange(timeRange) }),
    enabled: !!firstCrypto
  });

  const { data: secondCandles, isLoading: secondLoading } = useQuery({
    queryKey: ['candles', secondCrypto?.symbol, timeRange],
    queryFn: () => fetchCandles(secondCrypto.symbol, { limit: getLimitForTimeRange(timeRange) }),
    enabled: !!secondCrypto
  });

  const { data: thirdCandles, isLoading: thirdLoading } = useQuery({
    queryKey: ['candles', thirdCrypto?.symbol, timeRange],
    queryFn: () => fetchCandles(thirdCrypto.symbol, { limit: getLimitForTimeRange(timeRange) }),
    enabled: !!thirdCrypto
  });

  const chartData = useMemo(() => {
    if (selectedCryptos.length === 0) return null;

    const results = [];

    if (firstCrypto && firstCandles?.results) {
      results.push({
        name: firstCrypto.name,
        symbol: firstCrypto.symbol,
        data: firstCandles.results.map((candle) => ({
          time: candle.time,
          price: candle.close || candle.open || 0
        })).reverse()
      });
    }

    if (secondCrypto && secondCandles?.results) {
      results.push({
        name: secondCrypto.name,
        symbol: secondCrypto.symbol,
        data: secondCandles.results.map((candle) => ({
          time: candle.time,
          price: candle.close || candle.open || 0
        })).reverse()
      });
    }

    if (thirdCrypto && thirdCandles?.results) {
      results.push({
        name: thirdCrypto.name,
        symbol: thirdCrypto.symbol,
        data: thirdCandles.results.map((candle) => ({
          time: candle.time,
          price: candle.close || candle.open || 0
        })).reverse()
      });
    }

    return results.length > 0 ? results : null;
  }, [selectedCryptos, firstCandles, secondCandles, thirdCandles]);

  const formatNumber = (num) => {
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
    return `$${num.toFixed(2)}`;
  };

  const exportToCSV = () => {
    if (isGuest) {
      setShowLoginModal(true);
      return;
    }
    const headers = ['Name', 'Symbol', 'Price', 'Change 24h', 'Volume', 'Market Cap', 'Exchange'];
    const rows = filteredCryptos.map((c) => [
      c.name,
      c.symbol,
      c.price.toFixed(4),
      c.change24h.toFixed(2),
      c.volume.toFixed(2),
      c.marketCap.toFixed(2),
      c.exchange
    ]);
    const csv = [headers, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'crypto_data.csv';
    a.click();
  };

  const exportToJSON = () => {
    if (isGuest) {
      setShowLoginModal(true);
      return;
    }
    const json = JSON.stringify(filteredCryptos, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'crypto_data.json';
    a.click();
  };

  const handleTechnicalAnalysisClick = () => {
    if (isGuest) {
      setShowLoginModal(true);
      return;
    }
    setShowSymbolInput(true);
  };

  const fetchTechnicalAnalysis = async (symbol, timeframe = '1d') => {
    if (!symbol || symbol.trim() === '') {
      alert('Ве молиме внесете симбол');
      return;
    }
    setAnalysisLoading(true);
    try {
      const response = await api.get(`/technical-analysis/${symbol.toUpperCase()}/?timeframe=${timeframe}`);
      setAnalysisData(response.data);
      setShowSymbolInput(false);
      setShowAnalysisModal(true);
    } catch (error) {
      console.error('Failed to fetch technical analysis:', error);
      alert('Не успеа да се преземе техничката анализа. Можеби нема доволно податоци за овој симбол.');
    } finally {
      setAnalysisLoading(false);
    }
  };

  const getSignalColor = (signal) => {
    if (signal === 'BUY') return '#10b981';
    if (signal === 'SELL') return '#ef4444';
    return '#6b7280';
  };

  const getSignalBadge = (signal) => {
    return (
      <span style={{
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'bold',
        color: '#fff',
        backgroundColor: getSignalColor(signal)
      }}>
        {signal}
      </span>
    );
  };

  const renderSignalsChart = (signalSummary) => {
    if (!signalSummary) return null;

    return (
      <div style={{ marginBottom: '30px' }}>
        <h4 style={{ marginBottom: '10px', textAlign: 'center' }}>Сигнали Дистрибуција</h4>
        <div style={{ height: '200px' }}>
          <Bar
            data={{
              labels: ['BUY', 'HOLD', 'SELL'],
              datasets: [{
                label: 'Број на сигнали',
                data: [
                  signalSummary.buy_count,
                  signalSummary.hold_count,
                  signalSummary.sell_count
                ],
                backgroundColor: ['#10b981', '#6b7280', '#ef4444'],
                borderColor: ['#10b981', '#6b7280', '#ef4444'],
                borderWidth: 1
              }]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: (ctx) => `${ctx.label}: ${ctx.parsed.y} индикатори`
                  }
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  max: 10,
                  ticks: {
                    stepSize: 1,
                    color: '#9ca3af'
                  },
                  grid: { color: '#374151' }
                },
                x: {
                  grid: { display: false },
                  ticks: { color: '#9ca3af' }
                }
              }
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="explore-page">
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        message="Најавете се за да користите напредни функции"
      />

      <div className="explore-header">
        <h1>Истражување</h1>
        <p className="muted">Пребарај меѓу 1000-те најдобри криптовалути</p>
        {isGuest && (
          <div className="guest-notice" style={{ marginTop: '10px', padding: '10px', background: '#1f2937', borderRadius: '8px', border: '1px solid #374151' }}>
            <p style={{ margin: 0, color: '#9ca3af' }}>Како гостин, можете да прегледате податоци, но некои функции се оневозможени. Најавете се за целосен пристап.</p>
          </div>
        )}
      </div>

      <div className="explore-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Пребарај по име или симбол..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              // Optional: update URL as user types, or just keep local
              // setSearchParams({ search: e.target.value }); 
            }}
          />
        </div>

        <div className="filters-row">
          <div className="filter-group">
            <label>Берза:</label>
            <select
              value={selectedExchange}
              onChange={(e) => setSelectedExchange(e.target.value)}
            >
              <option value="all">Сите</option>
              {exchanges.map((exchange) => (
                <option key={exchange} value={exchange}>
                  {exchange}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Временски опсег:</label>
            <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
              <option value="1D">1 Ден</option>
              <option value="7D">7 Дена</option>
              <option value="1M">1 Месец</option>
              <option value="3M">3 Месеци</option>
              <option value="1Y">1 Година</option>
              <option value="10Y">10+ Години</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Волумен:</label>
            <select value={volumeFilter} onChange={(e) => setVolumeFilter(e.target.value)}>
              <option value="all">Сите</option>
              <option value="high">Висок</option>
              <option value="low">Низок</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Пазарна капитализација:</label>
            <select value={marketCapFilter} onChange={(e) => setMarketCapFilter(e.target.value)}>
              <option value="all">Сите</option>
              <option value="high">Висока</option>
              <option value="low">Ниска</option>
            </select>
          </div>
        </div>

        <div className="export-buttons">
          <button className="button button-primary" onClick={handleTechnicalAnalysisClick}>
            Техничка Анализа
          </button>
          <button className="button button-outline" onClick={exportToCSV}>
            Извези CSV
          </button>
          <button className="button button-outline" onClick={exportToJSON}>
            Извези JSON
          </button>
        </div>
      </div>

      <div className="compare-controls">
        <label>
          <input
            type="checkbox"
            checked={compareMode}
            onChange={(e) => {
              setCompareMode(e.target.checked);
              if (!e.target.checked) setSelectedCryptos([]);
            }}
          />
          Спореди до 3 криптовалути
        </label>
        {compareMode && selectedCryptos.length > 0 && (
          <div className="selected-cryptos">
            Избрани: {selectedCryptos.map((c) => c.symbol).join(', ')}
          </div>
        )}
      </div>

      {chartData && chartData.length > 0 && (
        <div className="chart-container">
          <h2>Историски графикон ({timeRange})</h2>
          {(firstLoading || secondLoading || thirdLoading) ? (
            <div className="chart-loading">Вчитување на податоци...</div>
          ) : (
            <div className="chart-wrapper" style={{ height: '400px', marginTop: '20px' }}>
              <Line
                data={{
                  labels: chartData[0]?.data.map(d => d.time) || [],
                  datasets: chartData.map((series, idx) => ({
                    label: `${series.name} (${series.symbol})`,
                    data: series.data.map(d => d.price),
                    borderColor: ['#60a5fa', '#34d399', '#f87171'][idx % 3],
                    backgroundColor: (context) => {
                      const ctx = context.chart.ctx;
                      const gradient = ctx.createLinearGradient(0, 0, 0, 400);
                      const colors = [
                        ['rgba(96, 165, 250, 0.3)', 'rgba(96, 165, 250, 0.0)'],
                        ['rgba(52, 211, 153, 0.3)', 'rgba(52, 211, 153, 0.0)'],
                        ['rgba(248, 113, 113, 0.3)', 'rgba(248, 113, 113, 0.0)']
                      ];
                      gradient.addColorStop(0, colors[idx % 3][0]);
                      gradient.addColorStop(1, colors[idx % 3][1]);
                      return gradient;
                    },
                    fill: true,
                    tension: 0.4,
                    pointRadius: 0,
                    pointHoverRadius: 4,
                    borderWidth: 2,
                  }))
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: true,
                      position: 'top',
                      labels: {
                        color: '#e2e8f0',
                        font: {
                          size: 12
                        }
                      }
                    },
                    tooltip: {
                      mode: 'index',
                      intersect: false,
                      callbacks: {
                        label: (ctx) => `${ctx.dataset.label}: $${ctx.parsed.y.toFixed(4)}`
                      }
                    }
                  },
                  scales: {
                    x: {
                      display: true,
                      grid: {
                        color: 'rgba(255, 255, 255, 0.05)',
                        drawBorder: false
                      },
                      ticks: {
                        color: '#94a3b8',
                        maxTicksLimit: 10
                      }
                    },
                    y: {
                      position: 'right',
                      grid: {
                        color: 'rgba(255, 255, 255, 0.1)',
                        drawBorder: false
                      },
                      ticks: {
                        color: '#94a3b8',
                        callback: (value) => `$${value.toFixed(2)}`
                      }
                    }
                  },
                  interaction: {
                    mode: 'nearest',
                    axis: 'x',
                    intersect: false
                  }
                }}
              />
            </div>
          )}
        </div>
      )}

      {tickersLoading ? (
        <div className="loading-state">Вчитување на податоци...</div>
      ) : (
        <div className="crypto-list">
          <table className="crypto-table">
            <thead>
              <tr>
                <th className="col-name">Криптовалута</th>
                <th className="col-price">Цена</th>
                <th className="col-change">Промена 24h</th>
                <th className="col-volume">Волумен</th>
                <th className="col-marketcap">Пазарна капитализација</th>
                <th className="col-exchange">Берза</th>
              </tr>
            </thead>
            <tbody>
              {filteredCryptos.length === 0 ? (
                <tr>
                  <td colSpan="6" className="empty-state">Нема резултати</td>
                </tr>
              ) : (
                filteredCryptos.map((crypto) => (
                  <tr
                    key={crypto.id}
                    className={selectedCryptos.find((c) => c.id === crypto.id) ? 'selected' : ''}
                    onClick={() => handleCryptoSelect(crypto)}
                  >
                    <td className="col-name">
                      <strong>{crypto.name}</strong> ({crypto.symbol})
                    </td>
                    <td className="col-price">${crypto.price.toFixed(4)}</td>
                    <td className={`col-change ${crypto.change24h >= 0 ? 'positive' : 'negative'}`}>
                      {crypto.change24h >= 0 ? '+' : ''}
                      {crypto.change24h.toFixed(2)}%
                    </td>
                    <td className="col-volume">{formatNumber(crypto.volume)}</td>
                    <td className="col-marketcap">{formatNumber(crypto.marketCap)}</td>
                    <td className="col-exchange">{crypto.exchange}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showSymbolInput && (
        <div className="modal-overlay" onClick={() => setShowSymbolInput(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Техничка Анализа</h2>
              <button className="modal-close" onClick={() => setShowSymbolInput(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '15px', color: '#9ca3af' }}>
                Внесете симбол на криптовалута (на пример: BTC-USD, ETH-USD)
              </p>
              <input
                type="text"
                placeholder="Симбол (на пример: BTC-USD)"
                value={symbolInput}
                onChange={(e) => setSymbolInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    fetchTechnicalAnalysis(symbolInput, analysisTimeframe);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '1px solid #374151',
                  borderRadius: '4px',
                  background: '#1f2937',
                  color: '#fff',
                  marginBottom: '15px'
                }}
                autoFocus
              />
              <div style={{ marginBottom: '20px' }}>
                <label style={{ marginRight: '10px', color: '#9ca3af' }}>Временски рамка:</label>
                <select
                  value={analysisTimeframe}
                  onChange={(e) => setAnalysisTimeframe(e.target.value)}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #374151', background: '#1f2937', color: '#fff' }}
                >
                  <option value="1d">1 Ден</option>
                  <option value="1w">1 Недела</option>
                  <option value="1m">1 Месец</option>
                </select>
              </div>
              <button
                className="button button-primary"
                onClick={() => fetchTechnicalAnalysis(symbolInput, analysisTimeframe)}
                disabled={analysisLoading}
                style={{ width: '100%' }}
              >
                {analysisLoading ? 'Вчитување...' : 'Прикажи Анализа'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAnalysisModal && analysisData && (
        <div className="modal-overlay" onClick={() => setShowAnalysisModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h2>Техничка Анализа - {analysisData.symbol}</h2>
              <button className="modal-close" onClick={() => setShowAnalysisModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '20px' }}>
                <label style={{ marginRight: '10px' }}>Временски рамка:</label>
                <select
                  value={analysisTimeframe}
                  onChange={(e) => {
                    setAnalysisTimeframe(e.target.value);
                    fetchTechnicalAnalysis(analysisData.symbol, e.target.value);
                  }}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #374151', background: '#1f2937', color: '#fff' }}
                >
                  <option value="1d">1 Ден</option>
                  <option value="1w">1 Недела</option>
                  <option value="1m">1 Месец</option>
                </select>
              </div>

              <div style={{ marginBottom: '20px', padding: '15px', background: '#1f2937', borderRadius: '8px' }}>
                <h3 style={{ marginBottom: '10px' }}>Вкупен Сигнал</h3>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                  {getSignalBadge(analysisData.overall_signal)}
                </div>

                <div style={{ marginTop: '5px', fontSize: '14px', color: '#9ca3af' }}>
                  Тековна цена: ${analysisData.current_price?.toFixed(4)} |
                  Волумен: ${analysisData.current_volume?.toFixed(2)}
                </div>
              </div>

              {renderSignalsChart(analysisData.signal_summary)}

              <div style={{ marginBottom: '30px' }}>
                <h4 style={{ marginBottom: '10px' }}>Сигнали Преглед</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                  <div style={{ padding: '15px', background: '#1f2937', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10b981' }}>
                      {analysisData.signal_summary.buy_count}
                    </div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '5px' }}>BUY Сигнали</div>
                  </div>
                  <div style={{ padding: '15px', background: '#1f2937', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#6b7280' }}>
                      {analysisData.signal_summary.hold_count}
                    </div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '5px' }}>HOLD Сигнали</div>
                  </div>
                  <div style={{ padding: '15px', background: '#1f2937', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ef4444' }}>
                      {analysisData.signal_summary.sell_count}
                    </div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '5px' }}>SELL Сигнали</div>
                  </div>
                </div>
              </div>

              <h3 style={{ marginBottom: '15px' }}>Осцилатори</h3>
              <table className="analysis-table" style={{ width: '100%', marginBottom: '30px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #374151' }}>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Индикатор</th>
                    <th style={{ padding: '10px', textAlign: 'right' }}>Вредност</th>
                    <th style={{ padding: '10px', textAlign: 'center' }}>Сигнал</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #374151' }}>
                    <td style={{ padding: '10px' }}>RSI</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>{analysisData.indicators.rsi.value?.toFixed(2) || 'N/A'}</td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{getSignalBadge(analysisData.signals.rsi)}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #374151' }}>
                    <td style={{ padding: '10px' }}>MACD</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>
                      {analysisData.indicators.macd.macd?.toFixed(4) || 'N/A'} / {analysisData.indicators.macd.signal?.toFixed(4) || 'N/A'}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{getSignalBadge(analysisData.signals.macd)}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #374151' }}>
                    <td style={{ padding: '10px' }}>Stochastic</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>
                      %K: {analysisData.indicators.stochastic.k?.toFixed(2) || 'N/A'}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{getSignalBadge(analysisData.signals.stochastic)}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #374151' }}>
                    <td style={{ padding: '10px' }}>ADX</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>{analysisData.indicators.adx.adx?.toFixed(2) || 'N/A'}</td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{getSignalBadge(analysisData.signals.adx)}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #374151' }}>
                    <td style={{ padding: '10px' }}>CCI</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>{analysisData.indicators.cci.value?.toFixed(2) || 'N/A'}</td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{getSignalBadge(analysisData.signals.cci)}</td>
                  </tr>
                </tbody>
              </table>

              <h3 style={{ marginBottom: '15px' }}>Движечки Просеци</h3>
              <table className="analysis-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #374151' }}>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Индикатор</th>
                    <th style={{ padding: '10px', textAlign: 'right' }}>Вредност</th>
                    <th style={{ padding: '10px', textAlign: 'center' }}>Сигнал</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #374151' }}>
                    <td style={{ padding: '10px' }}>SMA (20)</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>${analysisData.indicators.sma.value?.toFixed(4) || 'N/A'}</td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{getSignalBadge(analysisData.signals.sma)}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #374151' }}>
                    <td style={{ padding: '10px' }}>EMA (20)</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>${analysisData.indicators.ema.value?.toFixed(4) || 'N/A'}</td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{getSignalBadge(analysisData.signals.ema)}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #374151' }}>
                    <td style={{ padding: '10px' }}>WMA (20)</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>${analysisData.indicators.wma.value?.toFixed(4) || 'N/A'}</td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{getSignalBadge(analysisData.signals.wma)}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #374151' }}>
                    <td style={{ padding: '10px' }}>Bollinger Bands</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>
                      U: ${analysisData.indicators.bollinger.upper?.toFixed(4) || 'N/A'}<br />
                      M: ${analysisData.indicators.bollinger.middle?.toFixed(4) || 'N/A'}<br />
                      L: ${analysisData.indicators.bollinger.lower?.toFixed(4) || 'N/A'}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{getSignalBadge(analysisData.signals.bollinger)}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '10px' }}>Volume MA (20)</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>{analysisData.indicators.volume_ma.value?.toFixed(2) || 'N/A'}</td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{getSignalBadge(analysisData.signals.volume)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Explore;
